import type { TransactionDetailsDialogData } from '@peanuds/modules';
import { normalizeIban } from '@peanuds/shared';

import { EXTERNAL_TRANSFER_DEFAULT_SCHEME } from '../internal/send-money.constants';
import { toTodayIsoDate } from '../internal/send-money.presentation';
import type {
  ExternalTransferDraft,
  SendMoneyDraft,
  TransferScheme,
} from '../internal/send-money.types';
import { isExternalDraft } from '../internal/send-money.utils';
import type {
  RepeatPaymentBeneficiaryMatch,
  RepeatPaymentDestinationMatchKeys,
  RepeatPaymentOwnAccounts,
  RepeatPaymentPrefill,
} from './repeat-payment.types';

type OutgoingTransferDetails = NonNullable<TransactionDetailsDialogData['outgoingTransferDetails']>;
type RepeatableOutgoingDetails = TransactionDetailsDialogData & {
  outgoingTransferDetails: OutgoingTransferDetails;
};
type OwnAccountCandidate = {
  id: string;
  iban?: string | null;
};
type RepeatPaymentFields = {
  sourceAccountId: string;
  amount: number;
  description: string;
  scheme: TransferScheme;
  transferReasonId: string | null;
  selectedBeneficiaryName: string;
  selectedBankAccountIban: string | null;
  selectedBankAccountNumber: string | null;
};
type RepeatPaymentDraftPatch = Pick<
  ExternalTransferDraft,
  | Exclude<keyof RepeatPaymentPrefill, 'ui'>
  | 'flowType'
  | 'sourceAccountMode'
  | 'newRecipientFormValues'
  | 'savedRecipientBankAccountSelection'
>;
type RepeatPaymentDraftUiPatch = Required<ExternalTransferDraft['ui']>;

const toTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
};

const toNormalizedAccountNumber = (value: unknown): string | null => {
  return toTrimmedString(value)?.replace(/\s+/g, '') || null;
};

const toAmount = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

const toTransferScheme = (scheme: string | null | undefined): TransferScheme | null => {
  switch (scheme) {
    case 'swift':
      return 'swift';
    case 'sepa':
    case 'sepa-instant':
    case 'sepa-target-2':
    case 'sepa-target-2-pacs009':
      return EXTERNAL_TRANSFER_DEFAULT_SCHEME;
    default:
      return null;
  }
};

const toRoutingCodes = (
  routingCodes: { [key: string]: unknown } | null | undefined,
): Record<string, string> | null => {
  if (routingCodes === null || routingCodes === undefined) {
    return null;
  }

  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(routingCodes)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      normalized[key] = value.trim();
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : null;
};

const getRepeatPaymentFields = (details: RepeatableOutgoingDetails): RepeatPaymentFields | null => {
  const transfer = details.outgoingTransferDetails;
  const isInternal = transfer.scope === 'internal';
  const transferReasonId = toTrimmedString(transfer.transferReasonId);

  if (transferReasonId === null && !isInternal) {
    return null;
  }

  const sourceAccountId = toTrimmedString(transfer.source?.accountId);
  const amount = toAmount(transfer.amount ?? details.amount);
  const description = toTrimmedString(transfer.description);
  const scheme = isInternal
    ? EXTERNAL_TRANSFER_DEFAULT_SCHEME
    : toTransferScheme(transfer.scheme ?? details.scheme);
  const selectedBeneficiaryName = toTrimmedString(details.beneficiaryName);
  const selectedBankAccountIban = toTrimmedString(transfer.beneficiary?.account?.iban);
  const selectedBankAccountNumber = toTrimmedString(transfer.beneficiary?.account?.accountNumber);

  if (
    sourceAccountId === null ||
    amount === null ||
    description === null ||
    scheme === null ||
    selectedBeneficiaryName === null ||
    (selectedBankAccountIban === null && selectedBankAccountNumber === null)
  ) {
    return null;
  }

  return {
    sourceAccountId,
    amount,
    description,
    scheme,
    transferReasonId,
    selectedBeneficiaryName,
    selectedBankAccountIban,
    selectedBankAccountNumber,
  };
};

const toRepeatPaymentDraftPatch = ({
  ui: _ui,
  ...prefillFields
}: RepeatPaymentPrefill): RepeatPaymentDraftPatch => ({
  ...prefillFields,
  flowType: 'existing-contact',
  sourceAccountMode: 'manual',
  newRecipientFormValues: null,
  savedRecipientBankAccountSelection: null,
});

const toRepeatPaymentDraftUiPatch = (prefill: RepeatPaymentPrefill): RepeatPaymentDraftUiPatch => ({
  ...prefill.ui,
  selectedBeneficiaryTag: null,
  selectedBeneficiaryAvatar: null,
  selectedBankAccountNumber: prefill.ui.selectedBankAccountNumber ?? null,
});

const isOutgoingTransferDetails = (
  details: TransactionDetailsDialogData | null,
): details is RepeatableOutgoingDetails => {
  if (details === null || details.direction !== 'out') {
    return false;
  }

  return Boolean(details.outgoingTransferDetails);
};

const hasBeneficiaryBackedDestination = (transfer: OutgoingTransferDetails): boolean => {
  if (transfer.destination?.type === 'beneficiary-bank-account') {
    return true;
  }

  return (
    normalizeIban(transfer.beneficiary?.account?.iban ?? '').length > 0 ||
    toNormalizedAccountNumber(transfer.beneficiary?.account?.accountNumber) !== null
  );
};

const isOwnAccountDestination = (
  transfer: OutgoingTransferDetails,
  ownAccounts: RepeatPaymentOwnAccounts,
): boolean => {
  const destinationAccountId = toTrimmedString(transfer.destination?.accountId);
  if (destinationAccountId !== null && ownAccounts.accountIds.has(destinationAccountId)) {
    return true;
  }

  const iban = normalizeIban(transfer.beneficiary?.account?.iban ?? '');
  return iban.length > 0 && ownAccounts.ibans.has(iban);
};

const isDestinationBeneficiarySaved = (
  transfer: OutgoingTransferDetails,
  savedBeneficiaryIds: ReadonlySet<string> | null,
): boolean => {
  const beneficiaryId = toTrimmedString(transfer.destination?.beneficiaryId);
  if (beneficiaryId === null) {
    return true;
  }

  return savedBeneficiaryIds?.has(beneficiaryId) ?? false;
};

export const toRepeatPaymentOwnAccounts = (
  accounts: ReadonlyArray<OwnAccountCandidate>,
  isResolved: boolean,
): RepeatPaymentOwnAccounts => {
  const accountIds = new Set<string>();
  const ibans = new Set<string>();

  for (const account of accounts) {
    const accountId = toTrimmedString(account.id);
    if (accountId !== null) {
      accountIds.add(accountId);
    }

    const iban = normalizeIban(account.iban ?? '');
    if (iban.length > 0) {
      ibans.add(iban);
    }
  }

  return { isResolved, accountIds, ibans };
};

export const isRepeatableOutgoingTransferDetails = (
  details: TransactionDetailsDialogData | null,
  ownAccounts: RepeatPaymentOwnAccounts,
): details is RepeatableOutgoingDetails => {
  if (!isOutgoingTransferDetails(details)) {
    return false;
  }

  const transfer = details.outgoingTransferDetails;
  if (!hasBeneficiaryBackedDestination(transfer)) {
    return false;
  }

  if (transfer.scope !== 'internal') {
    return true;
  }

  if (!ownAccounts.isResolved) {
    return false;
  }

  return !isOwnAccountDestination(transfer, ownAccounts);
};

export const getRepeatPaymentDestinationMatchKeys = (
  details: TransactionDetailsDialogData,
): RepeatPaymentDestinationMatchKeys | null => {
  if (!isOutgoingTransferDetails(details)) {
    return null;
  }

  const transfer = details.outgoingTransferDetails;
  const normalizedIban = normalizeIban(transfer.beneficiary?.account?.iban ?? '');
  const matchKeys = {
    beneficiaryBankAccountId: toTrimmedString(transfer.destination?.beneficiaryBankAccountId),
    normalizedIban: normalizedIban.length > 0 ? normalizedIban : null,
    normalizedAccountNumber: toNormalizedAccountNumber(
      transfer.beneficiary?.account?.accountNumber,
    ),
  } satisfies RepeatPaymentDestinationMatchKeys;

  if (
    matchKeys.beneficiaryBankAccountId === null &&
    matchKeys.normalizedIban === null &&
    matchKeys.normalizedAccountNumber === null
  ) {
    return null;
  }

  return matchKeys;
};

export const getRepeatPaymentBeneficiaryNameCandidates = (
  details: TransactionDetailsDialogData,
): string[] => {
  if (!isOutgoingTransferDetails(details)) {
    return [];
  }

  const transfer = details.outgoingTransferDetails;
  const account = transfer.beneficiary?.account;
  const names = [
    details.beneficiaryName,
    details.summaryName,
    transfer.beneficiary?.fullName,
    account?.accountHolderName,
  ];

  const uniqueNames = new Set<string>();
  for (const name of names) {
    const normalizedName = toTrimmedString(name);
    if (normalizedName !== null) {
      uniqueNames.add(normalizedName);
    }
  }

  return Array.from(uniqueNames);
};

export const canRepeatOutgoingPayment = (
  details: TransactionDetailsDialogData | null,
  ownAccounts: RepeatPaymentOwnAccounts,
  savedBeneficiaryIds: ReadonlySet<string> | null,
): boolean => {
  if (!isRepeatableOutgoingTransferDetails(details, ownAccounts)) {
    return false;
  }

  if (!isDestinationBeneficiarySaved(details.outgoingTransferDetails, savedBeneficiaryIds)) {
    return false;
  }

  return getRepeatPaymentFields(details) !== null;
};

export const mapTransactionDetailsToRepeatPaymentPrefill = (
  details: TransactionDetailsDialogData,
  match: RepeatPaymentBeneficiaryMatch,
): RepeatPaymentPrefill | null => {
  if (!isOutgoingTransferDetails(details)) {
    return null;
  }

  const transfer = details.outgoingTransferDetails;
  const account = transfer.beneficiary?.account;
  const fields = getRepeatPaymentFields(details);

  if (fields === null) {
    return null;
  }

  const {
    selectedBeneficiaryName,
    selectedBankAccountIban,
    selectedBankAccountNumber,
    ...prefillFields
  } = fields;

  return {
    ...prefillFields,
    transferDate: toTodayIsoDate(),
    beneficiaryId: match.beneficiaryId,
    beneficiaryBankAccountId: match.beneficiaryBankAccountId,
    transferCustomReason: toTrimmedString(transfer.transferCustomReason),
    ui: {
      selectedBeneficiaryName,
      selectedBankAccountIban,
      ...(selectedBankAccountNumber === null ? {} : { selectedBankAccountNumber }),
      selectedBankName: toTrimmedString(account?.bankName),
      selectedBankAccountCountry: toTrimmedString(account?.country),
      selectedBankAccountCurrency:
        toTrimmedString(account?.currency) ?? toTrimmedString(transfer.currency),
      selectedBankRoutingCodes: toRoutingCodes(account?.routingCodes) ?? match.routingCodes,
    },
  };
};

export const applyRepeatPaymentPrefillToDraft = (
  draft: SendMoneyDraft,
  prefill: RepeatPaymentPrefill,
): boolean => {
  if (!isExternalDraft(draft)) {
    return false;
  }

  Object.assign(draft, toRepeatPaymentDraftPatch(prefill));
  Object.assign(draft.ui, toRepeatPaymentDraftUiPatch(prefill));

  return true;
};
